import React, { useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useParams } from 'react-router-dom';

import { useApiClient } from '@eluve/api-client-provider';
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Caption,
  ColDefBuilder,
  DataTable,
  Heading,
  Icon,
  type IconName,
  Stack,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  Text,
  tv,
  useToast,
} from '@eluve/components';
import { pluralize } from '@eluve/utils';

import { FlowStepper } from './components/FlowStepper';
import { type ImportRow } from './components/RowCard';
import { WhiteCard } from './components/WhiteCard';
import { useImportJob } from './hooks/useImportJob';
import { useImportRowMutations } from './hooks/useImportRowMutations';
import { useResolveJobTenantId } from './hooks/useResolveJobTenantId';
import {
  type RowWithIssues,
  projectRow,
  toRowWithIssues,
} from './screens/review-grouping';
import { PATIENT_IMPORT_ROUTE, errMsg, fetchAllImportRows } from './utils';

type StatTone = 'positive' | 'warning' | 'neutral';

const statCardStyles = tv({
  base: 'flex-1 rounded-lg border p-4',
  variants: {
    tone: {
      positive:
        'border-border-positive-subtle bg-bg-positive-subtle text-text-positive-subtle',
      warning:
        'border-border-warning-subtle bg-bg-warning-subtle text-text-warning-subtle',
      neutral: 'border-border-tertiary bg-bg-secondary text-text-tertiary',
    },
  },
});

const StatCard: React.FC<{
  tone: StatTone;
  icon: IconName;
  count: number;
  label: string;
  caption: string;
}> = ({ tone, icon, count, label, caption }) => (
  <div className={statCardStyles({ tone })} aria-label={`${label}: ${count}`}>
    <Stack gap={2}>
      <Stack direction="horizontal" gap={2} align="center">
        <Icon name={icon} size="sm" />
        <Text inline size="xs" weight="semibold" color="inherit">
          {label}
        </Text>
      </Stack>
      <Heading size="3xl" weight="semibold">
        {count}
      </Heading>
      <Caption>{caption}</Caption>
    </Stack>
  </div>
);

const ROW_COLUMNS = new ColDefBuilder<ImportRow>()
  .defaultSortable('rowNumber', 'Row #')
  .defaultSortable('patientId', 'Patient ID')
  .defaultSortable('firstName', 'First')
  .defaultSortable('lastName', 'Last')
  .defaultSortable('dateOfBirth', 'DOB')
  .defaultSortable('email', 'Email')
  .build();

export const PatientImportFinalizePage: React.FC = () => {
  const { jobId } = useParams<{ jobId: string }>();
  const tenantId = useResolveJobTenantId(jobId);

  if (!jobId) return null;
  if (!tenantId) return <Navigate to={PATIENT_IMPORT_ROUTE} replace />;

  return <FinalizeView jobId={jobId} tenantId={tenantId} />;
};

const FinalizeView: React.FC<{
  jobId: string;
  tenantId: string;
}> = ({ jobId, tenantId }) => {
  const job = useImportJob(tenantId, jobId);
  if (job.status !== 'REVIEW_READY') {
    return <Navigate to={`${PATIENT_IMPORT_ROUTE}/${jobId}`} replace />;
  }
  return <FinalizeForm jobId={jobId} tenantId={tenantId} />;
};

const FinalizeForm: React.FC<{
  jobId: string;
  tenantId: string;
}> = ({ jobId, tenantId }) => {
  const apiClient = useApiClient();
  const toast = useToast();
  const { finalizeJob } = useImportRowMutations(tenantId, jobId);

  const [rows, setRows] = useState<RowWithIssues[]>([]);
  const [blockingCount, setBlockingCount] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [finalizing, setFinalizing] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [importRes, rowsResult] = await Promise.all([
          apiClient.patientImport.getImport({ params: { tenantId, jobId } }),
          fetchAllImportRows(apiClient, tenantId, jobId),
        ]);
        if (cancelled) return;
        if (importRes.status !== 200) {
          throw new Error(`Failed to load import (${importRes.status})`);
        }
        const next = rowsResult.rows.map((r) => toRowWithIssues(r));
        setRows(next);
        setBlockingCount(importRes.body.counts.blocking);
        setLoaded(true);
      } catch (err) {
        if (cancelled) return;
        toast.error(`Failed to load finalize summary: ${errMsg(err)}`);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [apiClient, tenantId, jobId, toast]);

  const buckets = useMemo(() => {
    const willCreate: ImportRow[] = [];
    const willUpdate: ImportRow[] = [];
    const willKeepUnchanged: ImportRow[] = [];
    const willSkip: ImportRow[] = [];
    for (const row of rows) {
      const bucket = projectRow(row);
      if (bucket === 'willCreate') willCreate.push(row);
      else if (bucket === 'willUpdate') willUpdate.push(row);
      else if (bucket === 'willKeepUnchanged') willKeepUnchanged.push(row);
      else if (bucket === 'willSkip') willSkip.push(row);
    }
    return { willCreate, willUpdate, willKeepUnchanged, willSkip };
  }, [rows]);

  const onConfirm = async () => {
    setFinalizing(true);
    try {
      const result = await finalizeJob();
      if (!result.ok) {
        setBlockingCount(result.blockingCount);
        return;
      }
    } finally {
      setFinalizing(false);
    }
  };

  const reviewHref = `${PATIENT_IMPORT_ROUTE}/${jobId}`;
  const hasBlockingIssues = loaded && blockingCount > 0;
  const blockingDisabled =
    !loaded ||
    finalizing ||
    hasBlockingIssues ||
    buckets.willCreate.length +
      buckets.willUpdate.length +
      buckets.willKeepUnchanged.length ===
      0;

  const willCommit = buckets.willCreate.length + buckets.willUpdate.length;
  const confirmLabel =
    willCommit > 0
      ? `Confirm · import ${willCommit} patients`
      : 'Confirm · no changes';

  return (
    <Stack gap={6} align="stretch" className="mx-auto max-w-7xl">
      <Stack direction="horizontal" align="start" justify="between" gap={4}>
        <Stack gap={1}>
          <Link to={reviewHref}>
            <Button variant="ghost" size="sm">
              <Icon name="ArrowLeft" size="auto" />
              Back to review
            </Button>
          </Link>
          <Heading size="3xl" weight="semibold">
            Finalize import
          </Heading>
          <Text color="tertiary" size="sm">
            Review your patient data before confirming the import.
          </Text>
        </Stack>
        <Button onClick={onConfirm} disabled={blockingDisabled}>
          {finalizing ? 'Finalizing…' : confirmLabel}
          {!finalizing ? <Icon name="ArrowRight" size="auto" /> : null}
        </Button>
      </Stack>

      <FlowStepper current="finalize" />

      {hasBlockingIssues ? (
        <Alert variant="destructive">
          <Icon name="TriangleAlert" />
          <AlertTitle>
            {pluralize(blockingCount, 'blocking issue')} unresolved
          </AlertTitle>
          <AlertDescription>
            Resolve every blocking row in Review before finalizing. Open{' '}
            <Link to={reviewHref} className="underline">
              Back to review
            </Link>{' '}
            to fix them.
          </AlertDescription>
        </Alert>
      ) : null}

      <Stack direction="horizontal" gap={3}>
        <StatCard
          tone="positive"
          icon="Plus"
          count={buckets.willCreate.length}
          label="WILL CREATE"
          caption="new patients added"
        />
        <StatCard
          tone="warning"
          icon="Pencil"
          count={buckets.willUpdate.length}
          label="WILL UPDATE"
          caption="existing records changed"
        />
        <StatCard
          tone="neutral"
          icon="CircleCheck"
          count={buckets.willKeepUnchanged.length}
          label="ALREADY IMPORTED"
          caption="no changes needed"
        />
        <StatCard
          tone="neutral"
          icon="X"
          count={buckets.willSkip.length}
          label="WILL SKIP"
          caption="rows not imported"
        />
      </Stack>

      <WhiteCard className="p-0">
        <Tabs defaultValue="create">
          <TabsList
            variant="line"
            className="border-b border-border-tertiary px-4"
          >
            <TabsTrigger value="create">
              Create new ({buckets.willCreate.length})
            </TabsTrigger>
            <TabsTrigger value="update">
              Update existing ({buckets.willUpdate.length})
            </TabsTrigger>
            <TabsTrigger value="unchanged">
              Already imported ({buckets.willKeepUnchanged.length})
            </TabsTrigger>
            <TabsTrigger value="skip">
              Skipped ({buckets.willSkip.length})
            </TabsTrigger>
          </TabsList>
          <TabsContent value="create" className="px-4 pb-4">
            <DataTable
              data={buckets.willCreate}
              columns={ROW_COLUMNS}
              isPaginated={false}
            />
          </TabsContent>
          <TabsContent value="update" className="px-4 pb-4">
            <DataTable
              data={buckets.willUpdate}
              columns={ROW_COLUMNS}
              isPaginated={false}
            />
          </TabsContent>
          <TabsContent value="unchanged" className="px-4 pb-4">
            <DataTable
              data={buckets.willKeepUnchanged}
              columns={ROW_COLUMNS}
              isPaginated={false}
            />
          </TabsContent>
          <TabsContent value="skip" className="px-4 pb-4">
            <DataTable
              data={buckets.willSkip}
              columns={ROW_COLUMNS}
              isPaginated={false}
            />
          </TabsContent>
        </Tabs>
      </WhiteCard>

      {!loaded ? <Text color="tertiary">Loading…</Text> : null}
    </Stack>
  );
};
